<!-- Ported from the Polaris kit: src/product/components/UserAvatar.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first. -->
<script setup>
// A person, a team or a thing as a small picture: an image, initials or an icon, in a circle or a rounded square, with an availability
// dot and the mark of a channel; where the picture can be changed, hovering or focusing it offers to upload or remove. A composition of
// the registry Avatar. Beside a name it is decoration (`decorative`); alone it is named, with its availability in words.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TrashCan16, Upload16 } from '@carbon/icons-vue';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
// The kit marks a channel with its brand logo (a whole asset system this app does not carry); the app's own
// ChannelIcon already draws every channel (brand logo included) from an inbox's channel_type.
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { initials } from './initials';

const props = defineProps({
  name: { type: String, required: true },
  src: { type: String, default: '' },
  size: { type: Number, default: 32 }, // px
  shape: {
    type: String,
    default: 'circle',
    validator: value => ['circle', 'rounded'].includes(value),
  },
  status: {
    type: String,
    default: '',
    validator: value => ['', 'online', 'busy', 'offline'].includes(value),
  },
  icon: { type: [Object, Function], default: null }, // a Carbon icon: a team or a thing instead of a person
  channel: { type: String, default: '' }, // channel_type do app, por exemplo 'Channel::Whatsapp'
  tinted: Boolean, // initials on a tint that follows the name; neutral otherwise
  editable: Boolean, // upload and remove on hover or focus (from 48px)
  state: {
    type: String,
    default: 'idle',
    validator: value => ['idle', 'uploading', 'error'].includes(value),
  },
  decorative: Boolean,
});
defineEmits(['upload', 'remove']);

const { t } = useI18n();
const words = computed(() => ({
  online: t('POLARIS.USER_AVATAR.ONLINE'),
  busy: t('POLARIS.USER_AVATAR.BUSY'),
  offline: t('POLARIS.USER_AVATAR.OFFLINE'),
}));
const label = computed(() =>
  [
    props.name,
    words.value[props.status],
    props.state === 'error' ? t('POLARIS.USER_AVATAR.UPLOAD_FAILED') : '',
  ]
    .filter(Boolean)
    .join(', ')
);
const box = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));
const round = computed(() =>
  props.shape === 'circle' ? 'rounded-full' : 'rounded-[22%]'
);
// One tint out of the five chart colors, chosen by the name and softened into the surface, so the initials keep the text color.
const tint = computed(
  () =>
    `var(--polaris-chart-${([...props.name].reduce((sum, letter) => sum + letter.charCodeAt(0), 0) % 5) + 1})`
);
const tintStyle = computed(() =>
  props.tinted
    ? {
        backgroundColor: `color-mix(in oklch, ${tint.value} 24%, var(--polaris-card))`,
      }
    : {}
);
const text = computed(() => {
  if (props.size <= 24) return 'type-polaris-small-label';
  if (props.size <= 40) return 'type-polaris-label';
  if (props.size <= 56) return 'type-polaris-card-title';
  return 'type-polaris-section-title';
});
const dotSize = computed(() => {
  if (props.size <= 24) return 8;
  if (props.size <= 40) return 10;
  return 12;
});
const dot = computed(() => ({
  width: `${dotSize.value}px`,
  height: `${dotSize.value}px`,
}));
const dotColor = {
  online: 'bg-polaris-success-foreground',
  busy: 'bg-polaris-warning-foreground',
  offline: 'bg-polaris-muted-foreground',
};
</script>

<template>
  <span
    class="group/avatar relative inline-flex shrink-0"
    :style="box"
    data-user-avatar
    :data-shape="shape"
    :data-status="status || undefined"
    :data-state="state"
    :role="decorative ? undefined : 'img'"
    :aria-label="decorative ? undefined : label"
    :aria-hidden="decorative ? 'true' : undefined"
  >
    <Avatar
      class="size-full"
      :class="[
        round,
        state === 'error' ? 'ring-[2px] ring-polaris-destructive' : '',
      ]"
      aria-hidden="true"
    >
      <AvatarImage v-if="src" :src="src" alt="" />
      <AvatarFallback
        class="size-full"
        :class="[round, text]"
        :style="tintStyle"
      >
        <component :is="icon" v-if="icon" aria-hidden="true" />
        <template v-else-if="state !== 'uploading'">{{
          initials(name)
        }}</template>
      </AvatarFallback>
    </Avatar>
    <span
      v-if="status"
      class="absolute right-0 bottom-0 rounded-full ring-[2px] ring-polaris-background"
      :class="dotColor[status]"
      :style="dot"
      aria-hidden="true"
    />
    <span
      v-if="channel"
      class="absolute bottom-0 left-0 grid -translate-x-1/4 translate-y-1/4 place-items-center rounded-full bg-polaris-card p-px ring-[2px] ring-polaris-background"
      ><ChannelIcon
        :inbox="{ channel_type: channel }"
        use-brand-icon
        :style="{
          width: `${Math.max(12, Math.round(size * 0.35))}px`,
          height: `${Math.max(12, Math.round(size * 0.35))}px`,
        }"
    /></span>
    <span
      v-if="editable || state === 'uploading'"
      class="absolute inset-0 flex items-center justify-center gap-1 bg-polaris-foreground/70 opacity-0 transition-opacity focus-within:opacity-100 group-hover/avatar:opacity-100"
      :class="[round, state === 'uploading' ? 'opacity-100' : '']"
    >
      <Spinner v-if="state === 'uploading'" class="text-polaris-background" />
      <template v-else>
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          class="size-6 rounded-full p-0"
          :aria-label="t('POLARIS.USER_AVATAR.CHANGE_PICTURE', { name })"
          @click="$emit('upload')"
          ><Upload16 aria-hidden="true"
        /></Button>
        <Button
          v-if="src"
          type="button"
          variant="secondary"
          size="icon-sm"
          class="size-6 rounded-full p-0"
          :aria-label="t('POLARIS.USER_AVATAR.REMOVE_PICTURE', { name })"
          @click="$emit('remove')"
          ><TrashCan16 aria-hidden="true"
        /></Button>
      </template>
    </span>
  </span>
</template>
